import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { name: "HOME", to: "/" },
  { name: "ABOUT US", to: "/about" },
  { name: "TEAM", to: "/team" },
  { name: "MENTORSHIP", to: "/mentorship" },
  { name: "TESTIMONIALS", to: "/testimonials" },
  { name: "FAQ", to: "/faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleAnchor = (e, to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname === "/") {
        e.preventDefault();
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "pt-3" : "pt-6"
      }`}
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className={`nav-pill flex items-center justify-between px-5 py-3 md:px-8 md:py-3.5 ${open ? "rounded-b-none" : ""}`}>
          <Link
            to="/"
            data-testid="nav-logo-link"
            className="font-mono-serif text-[13px] md:text-sm tracking-[0.35em] text-white"
          >
            THE SYNDICATE
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                onClick={(e) => handleAnchor(e, item.to)}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="nav-link font-mono-serif text-[11px] tracking-[0.25em] text-white/80 hover:text-white"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Join Now CTA */}
          <Link
            to="/pricing"
            data-testid="nav-join-now-btn"
            className="hidden md:inline-flex items-center justify-center btn-shimmer text-black font-mono-serif text-[11px] tracking-[0.25em] px-5 py-2.5 rounded-full hover:opacity-90 transition"
          >
            JOIN NOW
          </Link>

          {/* Mobile menu button */}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div
            data-testid="nav-mobile-menu"
            className="md:hidden nav-pill rounded-t-none border-t-0 px-6 py-6 flex flex-col gap-5"
          >
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                onClick={(e) => handleAnchor(e, item.to)}
                data-testid={`nav-mobile-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="font-mono-serif text-xs tracking-[0.25em] text-white/85"
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/pricing"
              data-testid="nav-mobile-join-now-btn"
              className="btn-shimmer text-black font-mono-serif text-xs tracking-[0.25em] px-5 py-3 rounded-full text-center"
            >
              JOIN NOW
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
